import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import api, { adminAPI, submissionsAPI, resourcesAPI, uploadAPI, metricsAPI, claimsAPI } from '../lib/api';
import { 
    exportToExcel, 
    exportToPDF, 
    formatResourcesForExport, 
    formatMembersForExport,
    resourcePDFColumns,
    memberPDFColumns 
} from '../lib/exportUtils';
import { toast } from 'sonner';
import {
    LayoutDashboard,
    FileText,
    CheckCircle,
    XCircle,
    Clock,
    Building2,
    Search,
    Eye,
    Trash2,
    Star,
    Loader2,
    MapPin,
    ExternalLink,
    Upload,
    Pencil,
    MessageSquare,
    AlertCircle,
    X,
    FileCheck,
    Newspaper,
    Link as LinkIcon,
    FileUp,
    Plus,
    Download,
    FileSpreadsheet,
    Users,
    User,
    BarChart3,
    TrendingUp,
    ShieldCheck,
    Shield,
    UserPlus,
    RefreshCw,
    Mail,
    Database
} from 'lucide-react';

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming", "National"
];

const ORGANIZATION_TYPES = [
    "Employer", "Training Provider", "Service Provider", "Support Organization",
    "Government", "Foundation or Private Funder", "Higher Education",
    "Parents & Caregivers", "Workforce Alliance", "High School", "Transportation"
];

const MOU_STATUSES = [
    "Requested", "In Progress", "Approved", "Rejected", "Signed", "Expired"
];

const MOU_STATUS_COLORS = {
    "Requested": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
    "Approved": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
    "Rejected": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    "Signed": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    "Expired": "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
};

const NEWS_CATEGORIES = ["Reports", "Articles", "Videos", "Press Releases", "Research", "Events", "Other"];

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [activeTab, setActiveTab] = useState('users');
    const [submissions, setSubmissions] = useState([]);
    const [resources, setResources] = useState([]);
    const [newsResources, setNewsResources] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const fileInputRef = useRef(null);
    const newsFileInputRef = useRef(null);

    // Dialog states
    const [viewSubmission, setViewSubmission] = useState(null);
    const [editResource, setEditResource] = useState(null);
    const [deleteResource, setDeleteResource] = useState(null);
    const [rejectNotes, setRejectNotes] = useState('');
    const [infoRequestMessage, setInfoRequestMessage] = useState('');
    const [showInfoRequest, setShowInfoRequest] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [importStep, setImportStep] = useState('upload'); // 'upload' | 'preview' | 'result'
    
    // News CMS states
    const [editNews, setEditNews] = useState(null);
    const [deleteNews, setDeleteNews] = useState(null);
    const [newNewsItem, setNewNewsItem] = useState({
        title: '',
        source: '',
        category: 'Articles',
        resource_type: 'link',
        url: '',
        file_url: '',
        preview_image: '',
        description: '',
        is_published: true
    });
    const [showAddNews, setShowAddNews] = useState(false);
    const [uploadingNewsFile, setUploadingNewsFile] = useState(false);
    
    // Members data for export
    const [members, setMembers] = useState([]);
    const [exporting, setExporting] = useState(false);
    
    // Fix directory visibility
    const [fixingDirectory, setFixingDirectory] = useState(false);
    const [directoryStatus, setDirectoryStatus] = useState(null);

    const checkDirectoryStatus = async () => {
        try {
            const response = await api.get('/admin/resources/status-check');
            setDirectoryStatus(response.data);
        } catch (e) {
            console.error(e);
        }
    };

    const handleApproveAll = async () => {
        if (!window.confirm('This will make all resources visible in the public directory. Continue?')) return;
        setFixingDirectory(true);
        try {
            const response = await api.post('/admin/resources/approve-all');
            toast.success(`${response.data.updated} resources approved and now visible in directory`);
            setDirectoryStatus(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to approve resources');
        } finally {
            setFixingDirectory(false);
        }
    };

    // User management
    const [allUsers, setAllUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'member', chapter_region: '' });
    const [creatingUser, setCreatingUser] = useState(false);

    // Metrics management
    const [metricsSubmissions, setMetricsSubmissions] = useState([]);
    const [loadingMetrics, setLoadingMetrics] = useState(false);
    const [metricsFilter, setMetricsFilter] = useState('all');
    const [adminMetricsDialog, setAdminMetricsDialog] = useState(false);
    const [adminMetricsOrg, setAdminMetricsOrg] = useState('');
    const [adminMetricsForm, setAdminMetricsForm] = useState({
        year: new Date().getFullYear().toString(),
        employed_count: '',
        impacted_count: '',
        completion_rate: '',
        placement_rate: '',
        notes: '',
    });

    // Claims management
    const [claims, setClaims] = useState([]);
    const [loadingClaims, setLoadingClaims] = useState(false);
    const [claimsFilter, setClaimsFilter] = useState('pending');
    const [assignDialog, setAssignDialog] = useState(false);
    const [assignOrgId, setAssignOrgId] = useState('');
    const [assignEmail, setAssignEmail] = useState('');

    // Chapters management
    const [chapters, setChapters] = useState([]);
    const [loadingChapters, setLoadingChapters] = useState(false);
    const [showAddChapter, setShowAddChapter] = useState(false);
    const [editingChapter, setEditingChapter] = useState(null);
    const [chapterForm, setChapterForm] = useState({ name: '', state: '', description: '', director_id: '' });

    // Email preview
    const [emailPreview, setEmailPreview] = useState(null);
    const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);

    const templateNames = {
        nomination: 'Nomination Email',
        membership_application: 'Membership Application',
        welcome: 'Welcome Email',
        needs_info: 'Needs More Info',
        approval: 'Approval Email',
    };

    const handlePreviewEmail = async (templateKey) => {
        setEmailPreviewLoading(true);
        setEmailPreview(null);
        try {
            const res = await api.get(`/admin/email-preview/${templateKey}`);
            setEmailPreview({ ...res.data, name: templateNames[templateKey] || templateKey });
        } catch (error) {
            toast.error('Failed to load email preview');
        } finally {
            setEmailPreviewLoading(false);
        }
    };

    // Partners management
    const [partnersList, setPartnersList] = useState([]);
    const [loadingPartners, setLoadingPartners] = useState(false);
    const [showAddPartner, setShowAddPartner] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [partnerForm, setPartnerForm] = useState({
        name: '', description: '', organization: '', logo_url: '', website: '',
        databases: [{ db_id: '', name: '', description: '', url: '', type: '' }]
    });

    const fetchPartners = async () => {
        setLoadingPartners(true);
        try {
            const res = await api.get('/admin/partner-databases');
            setPartnersList(res.data);
        } catch (error) {
            console.error('Failed to fetch partners:', error);
        } finally {
            setLoadingPartners(false);
        }
    };

    const handleSavePartner = async () => {
        if (!partnerForm.name.trim()) {
            toast.error('Partner name is required');
            return;
        }
        const cleanDbs = partnerForm.databases
            .filter(d => d.name.trim())
            .map((d, i) => ({ ...d, db_id: d.db_id || `db_${Date.now()}_${i}` }));

        const payload = { ...partnerForm, databases: cleanDbs };
        try {
            if (editingPartner) {
                await api.put(`/admin/partner-databases/${editingPartner.partner_id}`, payload);
                toast.success('Partner updated');
            } else {
                await api.post('/admin/partner-databases', payload);
                toast.success('Partner added');
            }
            setShowAddPartner(false);
            setEditingPartner(null);
            setPartnerForm({ name: '', description: '', organization: '', logo_url: '', website: '', databases: [{ db_id: '', name: '', description: '', url: '', type: '' }] });
            fetchPartners();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save partner');
        }
    };

    const handleDeletePartner = async (partnerId) => {
        if (!window.confirm('Delete this partner?')) return;
        try {
            await api.delete(`/admin/partner-databases/${partnerId}`);
            toast.success('Partner deleted');
            fetchPartners();
        } catch (error) {
            toast.error('Failed to delete partner');
        }
    };

    const openEditPartner = (partner) => {
        setEditingPartner(partner);
        setPartnerForm({
            name: partner.name || '',
            description: partner.description || '',
            organization: partner.organization || '',
            logo_url: partner.logo_url || '',
            website: partner.website || '',
            databases: partner.databases?.length ? partner.databases : [{ db_id: '', name: '', description: '', url: '', type: '' }]
        });
        setShowAddPartner(true);
    };

    useEffect(() => {
        if (!authLoading && (!user || !user.is_admin)) {
            navigate('/');
        }
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (user?.is_admin) {
            fetchData();
            fetchNewsResources();
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [subsRes, resRes, statsRes] = await Promise.all([
                submissionsAPI.getAll(),
                adminAPI.getResources(),
                resourcesAPI.getStats()
            ]);
            setSubmissions(subsRes.data);
            setResources(resRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const fetchNewsResources = async () => {
        try {
            const response = await adminAPI.getNews({ limit: 100 });
            setNewsResources(response.data.news || []);
        } catch (error) {
            console.error('Error fetching news:', error);
        }
    };

    const fetchMembers = async () => {
        try {
            const response = await adminAPI.getMembers();
            setMembers(response.data);
        } catch (error) {
            console.error('Error fetching members:', error);
            toast.error('Failed to load members');
        }
    };

    // Export handlers
    const handleExportResources = async (format) => {
        setExporting(true);
        try {
            const formattedData = formatResourcesForExport(resources);
            const filename = `HAAPE_Resources_${new Date().toISOString().split('T')[0]}`;
            
            if (format === 'excel') {
                exportToExcel(formattedData, filename, 'Resources');
                toast.success('Resources exported to Excel');
            } else if (format === 'pdf') {
                exportToPDF(formattedData, filename, 'HAAPE Hub - Public Directory', resourcePDFColumns);
                toast.success('Resources exported to PDF');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export resources');
        } finally {
            setExporting(false);
        }
    };

    const handleExportMembers = async (format) => {
        setExporting(true);
        try {
            // Use admin endpoint to get all users with full data
            const response = await adminAPI.getUsers();
            const memberData = response.data;
            
            const formattedData = formatMembersForExport(memberData);
            const filename = `HAAPE_Members_${new Date().toISOString().split('T')[0]}`;
            
            if (format === 'excel') {
                exportToExcel(formattedData, filename, 'Members');
                toast.success('Members exported to Excel');
            } else if (format === 'pdf') {
                exportToPDF(formattedData, filename, 'HAAPE Hub - Member Directory', memberPDFColumns);
                toast.success('Members exported to PDF');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export members');
        } finally {
            setExporting(false);
        }
    };

    // User management functions
    const fetchAllUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await adminAPI.getUsers();
            setAllUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchMetricsSubmissions = async () => {
        setLoadingMetrics(true);
        try {
            const params = metricsFilter !== 'all' ? { status: metricsFilter } : {};
            const response = await metricsAPI.getAllSubmissions(params);
            setMetricsSubmissions(response.data || []);
        } catch (error) {
            console.error('Error fetching metrics:', error);
            toast.error('Failed to load metrics submissions');
        } finally {
            setLoadingMetrics(false);
        }
    };

    const handleApproveMetrics = async (metricsId) => {
        setProcessing(true);
        try {
            await metricsAPI.approve(metricsId);
            toast.success('Metrics approved and organization updated');
            fetchMetricsSubmissions();
        } catch (error) {
            toast.error('Failed to approve metrics');
        } finally {
            setProcessing(false);
        }
    };

    const handleRejectMetrics = async (metricsId) => {
        setProcessing(true);
        try {
            await metricsAPI.reject(metricsId);
            toast.success('Metrics rejected');
            fetchMetricsSubmissions();
        } catch (error) {
            toast.error('Failed to reject metrics');
        } finally {
            setProcessing(false);
        }
    };

    const handleAdminSubmitMetrics = async () => {
        if (!adminMetricsOrg) {
            toast.error('Please select an organization');
            return;
        }
        const payload = {
            year: parseInt(adminMetricsForm.year),
            employed_count: adminMetricsForm.employed_count ? parseInt(adminMetricsForm.employed_count) : null,
            impacted_count: adminMetricsForm.impacted_count ? parseInt(adminMetricsForm.impacted_count) : null,
            completion_rate: adminMetricsForm.completion_rate ? parseFloat(adminMetricsForm.completion_rate) : null,
            placement_rate: adminMetricsForm.placement_rate ? parseFloat(adminMetricsForm.placement_rate) : null,
            notes: adminMetricsForm.notes || null,
        };
        if (!payload.employed_count && !payload.impacted_count && !payload.completion_rate && !payload.placement_rate) {
            toast.error('Please fill in at least one metric');
            return;
        }
        setProcessing(true);
        try {
            await metricsAPI.submitMetrics(adminMetricsOrg, payload);
            toast.success('Metrics submitted and auto-approved');
            setAdminMetricsDialog(false);
            setAdminMetricsForm({ year: new Date().getFullYear().toString(), employed_count: '', impacted_count: '', completion_rate: '', placement_rate: '', notes: '' });
            setAdminMetricsOrg('');
            fetchMetricsSubmissions();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit metrics');
        } finally {
            setProcessing(false);
        }
    };

    // ============ CLAIMS FUNCTIONS ============
    const fetchClaims = async () => {
        setLoadingClaims(true);
        try {
            const params = claimsFilter !== 'all' ? { status: claimsFilter } : {};
            const response = await claimsAPI.getAllClaims(params);
            setClaims(response.data || []);
        } catch (error) {
            toast.error('Failed to load claims');
        } finally {
            setLoadingClaims(false);
        }
    };

    const handleApproveClaim = async (claimId) => {
        setProcessing(true);
        try {
            await claimsAPI.approveClaim(claimId);
            toast.success('Claim approved. Member now has edit access.');
            fetchClaims();
        } catch (error) {
            toast.error('Failed to approve claim');
        } finally {
            setProcessing(false);
        }
    };

    const handleRejectClaim = async (claimId) => {
        setProcessing(true);
        try {
            await claimsAPI.rejectClaim(claimId);
            toast.success('Claim rejected');
            fetchClaims();
        } catch (error) {
            toast.error('Failed to reject claim');
        } finally {
            setProcessing(false);
        }
    };

    const handleAssignOrg = async () => {
        if (!assignOrgId || !assignEmail) {
            toast.error('Please select an organization and enter an email');
            return;
        }
        setProcessing(true);
        try {
            await claimsAPI.assignOrg(assignOrgId, { email: assignEmail });
            toast.success('Organization assigned successfully');
            setAssignDialog(false);
            setAssignOrgId('');
            setAssignEmail('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to assign organization');
        } finally {
            setProcessing(false);
        }
    };

    // Chapter management functions
    const fetchChapters = async () => {
        setLoadingChapters(true);
        try {
            const [chaptersRes] = await Promise.all([
                api.get('/admin/chapters'),
                allUsers.length === 0 ? fetchAllUsers() : Promise.resolve()
            ]);
            setChapters(chaptersRes.data);
        } catch (error) {
            toast.error('Failed to load chapters');
        } finally {
            setLoadingChapters(false);
        }
    };

    const handleSaveChapter = async () => {
        if (!chapterForm.name || !chapterForm.state) {
            toast.error('Name and state are required');
            return;
        }
        const payload = { ...chapterForm };
        if (payload.director_id === 'none' || payload.director_id === '') {
            payload.director_id = null;
        }
        try {
            if (editingChapter) {
                await api.put(`/admin/chapters/${editingChapter.chapter_id}`, payload);
                toast.success('Chapter updated');
            } else {
                await api.post('/admin/chapters', payload);
                toast.success('Chapter created');
            }
            setShowAddChapter(false);
            setEditingChapter(null);
            setChapterForm({ name: '', state: '', description: '', director_id: '' });
            fetchChapters();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save chapter');
        }
    };

    const handleDeleteChapter = async (chapterId) => {
        if (!window.confirm('Delete this chapter? The director will be reverted to a regular member.')) return;
        try {
            await api.delete(`/admin/chapters/${chapterId}`);
            toast.success('Chapter deleted');
            fetchChapters();
        } catch (error) {
            toast.error('Failed to delete chapter');
        }
    };

    const openEditChapter = (chapter) => {
        setEditingChapter(chapter);
        setChapterForm({
            name: chapter.name,
            state: chapter.state,
            description: chapter.description || '',
            director_id: chapter.director_id || ''
        });
        setShowAddChapter(true);
    };

    const handleToggleAdmin = async (userId, currentIsAdmin) => {
        try {
            await api.put(`/admin/users/${userId}/role`, { is_admin: !currentIsAdmin, role: !currentIsAdmin ? 'admin' : 'member' });
            toast.success(currentIsAdmin ? 'Admin role removed' : 'Admin role granted');
            fetchAllUsers();
        } catch (error) {
            console.error('Error updating user role:', error);
            toast.error('Failed to update user role');
        }
    };

    const handleSetChapterDirector = async (userId, region) => {
        try {
            await api.put(`/admin/users/${userId}/role`, { 
                is_admin: false, 
                role: 'chapter_director', 
                chapter_region: region 
            });
            toast.success(`Chapter Director role assigned for ${region}`);
            fetchAllUsers();
        } catch (error) {
            console.error('Error setting chapter director:', error);
            toast.error('Failed to assign Chapter Director role');
        }
    };

    const handleRemoveChapterDirector = async (userId) => {
        try {
            await api.put(`/admin/users/${userId}/role`, { 
                is_admin: false, 
                role: 'member' 
            });
            toast.success('Chapter Director role removed');
            fetchAllUsers();
        } catch (error) {
            console.error('Error removing chapter director:', error);
            toast.error('Failed to remove Chapter Director role');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            return;
        }
        try {
            await api.delete(`/admin/users/${userId}`);
            toast.success('User deleted');
            fetchAllUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user');
        }
    };

    const handleApproveUser = async (userId, approve = true) => {
        try {
            await api.put(`/admin/users/${userId}/approve`, { approved: approve });
            toast.success(approve ? 'User membership approved' : 'User membership revoked');
            fetchAllUsers();
        } catch (error) {
            console.error('Error approving user:', error);
            toast.error('Failed to update user approval');
        }
    };

    const handleNotifyAdmins = async (type, name, detail = '') => {
        try {
            await api.post('/admin/notify', { type, name, detail });
            toast.success('Admin notification email sent');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send notification');
        }
    };

    const [sendingTestEmails, setSendingTestEmails] = useState(false);
    const handleSendTestJourneyEmails = async () => {
        setSendingTestEmails(true);
        try {
            const recipients = ['l.rothman@haape.org', 'c.alden@haape.org', 'tara.may@aspiritech.org'];
            const res = await adminAPI.sendTestJourneyEmails(recipients);
            toast.success(`Test emails sent to ${recipients.join(', ')}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send test emails');
        } finally {
            setSendingTestEmails(false);
        }
    };

    const handleCreateUser = async () => {
        if (!newUser.name || !newUser.email) {
            toast.error('Name and email are required');
            return;
        }
        if (newUser.password && newUser.password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        setCreatingUser(true);
        try {
            await api.post('/admin/users/create', newUser);
            toast.success(`Member "${newUser.name}" created successfully`);
            setShowCreateUser(false);
            setNewUser({ name: '', email: '', password: '', role: 'member', chapter_region: '' });
            fetchAllUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create member');
        } finally {
            setCreatingUser(false);
        }
    };



    const filteredUsers = allUsers.filter(u => 
        u.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        u.organization?.toLowerCase().includes(userSearchQuery.toLowerCase())
    );

    const handleCreateNews = async () => {
        if (!newNewsItem.title || !newNewsItem.source) {
            toast.error('Title and Source are required');
            return;
        }
        
        setProcessing(true);
        try {
            await adminAPI.createNews(newNewsItem);
            toast.success('Resource added successfully');
            setShowAddNews(false);
            setNewNewsItem({
                title: '', source: '', category: 'Articles', resource_type: 'link',
                url: '', file_url: '', preview_image: '', description: '', is_published: true
            });
            fetchNewsResources();
        } catch (error) {
            toast.error('Failed to add resource');
        } finally {
            setProcessing(false);
        }
    };

    const handleUpdateNews = async () => {
        if (!editNews) return;
        setProcessing(true);
        try {
            await adminAPI.updateNews(editNews.news_id, editNews);
            toast.success('Resource updated');
            setEditNews(null);
            fetchNewsResources();
        } catch (error) {
            toast.error('Failed to update resource');
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteNews = async () => {
        if (!deleteNews) return;
        setProcessing(true);
        try {
            await adminAPI.deleteNews(deleteNews.news_id);
            toast.success('Resource deleted');
            setDeleteNews(null);
            fetchNewsResources();
        } catch (error) {
            toast.error('Failed to delete resource');
        } finally {
            setProcessing(false);
        }
    };

    const handleNewsFileUpload = async (e, isEdit = false) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setUploadingNewsFile(true);
        try {
            const response = await uploadAPI.upload(file);
            if (isEdit && editNews) {
                setEditNews(prev => ({ ...prev, file_url: response.data.url }));
            } else {
                setNewNewsItem(prev => ({ ...prev, file_url: response.data.url }));
            }
            toast.success('File uploaded');
        } catch (error) {
            toast.error('Failed to upload file');
        } finally {
            setUploadingNewsFile(false);
        }
    };

    const handleApprove = async (submissionId) => {
        setProcessing(true);
        try {
            await submissionsAPI.approve(submissionId);
            toast.success('Submission approved and added to directory');
            setViewSubmission(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to approve submission');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async (submissionId) => {
        setProcessing(true);
        try {
            await submissionsAPI.reject(submissionId, rejectNotes);
            toast.success('Submission rejected');
            setViewSubmission(null);
            setRejectNotes('');
            fetchData();
        } catch (error) {
            toast.error('Failed to reject submission');
        } finally {
            setProcessing(false);
        }
    };

    const handleRequestInfo = async (submissionId) => {
        if (!infoRequestMessage.trim()) {
            toast.error('Please enter a message');
            return;
        }
        setProcessing(true);
        try {
            await submissionsAPI.requestInfo(submissionId, infoRequestMessage);
            toast.success('Information request sent');
            setViewSubmission(null);
            setInfoRequestMessage('');
            setShowInfoRequest(false);
            fetchData();
        } catch (error) {
            toast.error('Failed to send request');
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteResource = async () => {
        if (!deleteResource) return;
        setProcessing(true);
        try {
            await resourcesAPI.delete(deleteResource.resource_id);
            toast.success('Resource deleted');
            setDeleteResource(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to delete resource');
        } finally {
            setProcessing(false);
        }
    };

    const handleToggleFeatured = async (resource) => {
        try {
            await resourcesAPI.update(resource.resource_id, { 
                is_featured: !resource.is_featured 
            });
            toast.success(resource.is_featured ? 'Removed from featured' : 'Added to featured');
            fetchData();
        } catch (error) {
            toast.error('Failed to update resource');
        }
    };

    const handleUpdateResource = async () => {
        if (!editResource) return;
        setProcessing(true);
        try {
            await resourcesAPI.update(editResource.resource_id, {
                name: editResource.name,
                description: editResource.description,
                organization_types: editResource.organization_types,
                website: editResource.website,
                state: editResource.state,
                city: editResource.city,
                contact_name: editResource.contact_name,
                contact_email: editResource.contact_email,
                phone_number: editResource.phone_number,
                nd_population_served: editResource.nd_population_served,
                is_featured: editResource.is_featured,
            });
            toast.success('Resource updated');
            setEditResource(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to update resource');
        } finally {
            setProcessing(false);
        }
    };

    const handleUpdateMouStatus = async (resourceId, newStatus) => {
        try {
            await resourcesAPI.updateMouStatus(resourceId, newStatus);
            toast.success(`T&C status updated to ${newStatus}`);
            fetchData();
        } catch (error) {
            toast.error('Failed to update T&C status');
        }
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Please upload a CSV or XLSX file');
            return;
        }
        
        setUploadedFile(file);
        setPreviewLoading(true);
        setPreviewData(null);
        setImportResult(null);
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const response = await adminAPI.uploadPreview(formData);
            setPreviewData(response.data);
            setImportStep('preview');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to parse file');
            setImportStep('upload');
        } finally {
            setPreviewLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };
    
    const handleConfirmImport = async () => {
        if (!uploadedFile) return;
        
        setImporting(true);
        const formData = new FormData();
        formData.append('file', uploadedFile);
        
        try {
            const response = await adminAPI.uploadImport(formData);
            setImportResult(response.data);
            setImportStep('result');
            if (response.data.imported > 0) {
                toast.success(`Imported ${response.data.imported} organizations`);
                fetchData();
            }
        } catch (error) {
            toast.error('Import failed');
            setImportResult({ error: error.message || 'Import failed' });
            setImportStep('result');
        } finally {
            setImporting(false);
        }
    };
    
    const resetImport = () => {
        setImportStep('upload');
        setPreviewData(null);
        setImportResult(null);
        setUploadedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const filteredResources = resources.filter(r => 
        (r.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.state || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.city || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.description || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const pendingCount = submissions.filter(s => s.status === 'pending').length;
    const needsInfoCount = submissions.filter(s => s.status === 'needs_info').length;

    // Show loading while checking auth
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    // Show access denied if not admin
    if (!user?.is_admin) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="admin-access-denied">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                            <AlertCircle className="h-8 w-8 text-destructive" />
                        </div>
                        <CardTitle>Access Denied</CardTitle>
                        <CardDescription>
                            You don't have administrator privileges to access this page.
                            Please contact a HAAPE administrator if you believe this is an error.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                        <Button onClick={() => window.location.href = '/directory'}>
                            Go to Public Directory
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <h1 className="font-heading text-3xl font-bold mb-2 flex items-center gap-3">
                            <LayoutDashboard className="h-8 w-8 text-primary" />
                            Admin Dashboard
                        </h1>
                        <p className="text-muted-foreground">
                            Manage your hub: public directory, resource library, members, and chapters
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSendTestJourneyEmails}
                        disabled={sendingTestEmails}
                        data-testid="send-test-journey-emails-btn"
                        className="gap-2 shrink-0"
                    >
                        {sendingTestEmails ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        Send Test Journey Emails
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-primary/10 rounded-lg">
                                    <Building2 className="h-6 w-6 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">
                                        {loading ? '...' : stats?.total_resources || 0}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Total Resources</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-amber-500/10 rounded-lg">
                                    <Clock className="h-6 w-6 text-amber-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{loading ? '...' : pendingCount}</p>
                                    <p className="text-sm text-muted-foreground">Pending Reviews</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg">
                                    <AlertCircle className="h-6 w-6 text-orange-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{loading ? '...' : needsInfoCount}</p>
                                    <p className="text-sm text-muted-foreground">Needs Info</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-emerald-500/10 rounded-lg">
                                    <MapPin className="h-6 w-6 text-emerald-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">
                                        {loading ? '...' : stats?.states_covered || 0}
                                    </p>
                                    <p className="text-sm text-muted-foreground">States Covered</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="users" className="gap-2" data-testid="tab-users" onClick={fetchAllUsers}>
                            <Users className="h-4 w-4" />
                            Members
                        </TabsTrigger>
                        <TabsTrigger value="submissions" className="gap-2" data-testid="tab-submissions">
                            <FileText className="h-4 w-4" />
                            Submissions
                            {(pendingCount + needsInfoCount) > 0 && (
                                <Badge variant="destructive" className="ml-1 h-5 min-w-5 p-1 flex items-center justify-center text-xs">
                                    {pendingCount + needsInfoCount}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="resources" className="gap-2" data-testid="tab-resources">
                            <Building2 className="h-4 w-4" />
                            Public Directory
                        </TabsTrigger>
                        <TabsTrigger value="news" className="gap-2" data-testid="tab-news">
                            <Newspaper className="h-4 w-4" />
                            Resource Library
                        </TabsTrigger>
                        <TabsTrigger value="export" className="gap-2" data-testid="tab-export">
                            <Download className="h-4 w-4" />
                            Export
                        </TabsTrigger>
                        <TabsTrigger value="import" className="gap-2" data-testid="tab-import">
                            <Upload className="h-4 w-4" />
                            Import
                        </TabsTrigger>
                        <TabsTrigger value="metrics" className="gap-2" data-testid="tab-metrics" onClick={fetchMetricsSubmissions}>
                            <BarChart3 className="h-4 w-4" />
                            Metrics
                        </TabsTrigger>
                        <TabsTrigger value="claims" className="gap-2" data-testid="tab-claims" onClick={fetchClaims}>
                            <ShieldCheck className="h-4 w-4" />
                            Claims
                        </TabsTrigger>
                        <TabsTrigger value="chapters" className="gap-2" data-testid="tab-chapters" onClick={fetchChapters}>
                            <Shield className="h-4 w-4" />
                            Chapters
                        </TabsTrigger>
                        <TabsTrigger value="emails" className="gap-2" data-testid="tab-emails">
                            <Mail className="h-4 w-4" />
                            Email Preview
                        </TabsTrigger>
                        <TabsTrigger value="partners" className="gap-2" data-testid="tab-partners" onClick={fetchPartners}>
                            <Database className="h-4 w-4" />
                            Partners
                        </TabsTrigger>
                    </TabsList>

                    {/* Submissions Tab */}
                    <TabsContent value="submissions">
                        <Card>
                            <CardHeader>
                                <CardTitle>Resource Submissions</CardTitle>
                                <CardDescription>
                                    Review and approve community-submitted resources
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="space-y-4">
                                        {[...Array(3)].map((_, i) => (
                                            <Skeleton key={i} className="h-20 w-full" />
                                        ))}
                                    </div>
                                ) : submissions.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No submissions yet</p>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[500px]">
                                        <div className="space-y-3">
                                            {submissions.map((submission) => (
                                                <div 
                                                    key={submission.submission_id}
                                                    className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                                                    onClick={() => setViewSubmission(submission)}
                                                    data-testid={`submission-${submission.submission_id}`}
                                                >
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        {/* Submission Type Badge */}
                                                        {submission.submission_type === 'trusted_partner_nomination' ? (
                                                            <Badge className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold">MEMBER NOMINATION</Badge>
                                                        ) : submission.stakeholder_type || submission.stakeholder_types?.length ? (
                                                            <Badge className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold">PUBLIC DIRECTORY LISTING</Badge>
                                                        ) : (
                                                            <Badge className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold">RESOURCE</Badge>
                                                        )}
                                                        <h4 className="font-medium">{submission.name}</h4>
                                                        <Badge 
                                                            variant={
                                                                submission.status === 'pending' ? 'secondary' :
                                                                submission.status === 'needs_info' ? 'outline' :
                                                                submission.status === 'approved' ? 'default' : 'destructive'
                                                            }
                                                        >
                                                            {submission.status === 'needs_info' ? 'Needs Info' : submission.status}
                                                        </Badge>
                                                        {submission.stakeholder_type && (
                                                            <Badge variant="outline">
                                                                {submission.stakeholder_type}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                                        {submission.mission}
                                                    </p>
                                                    <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                                                        <span className="flex items-center gap-1">
                                                            <MapPin className="h-3 w-3" />
                                                            {submission.state}
                                                        </span>
                                                        <span>By: {submission.requester_name}</span>
                                                        <span>
                                                            {new Date(submission.created_at).toLocaleDateString()}
                                                        </span>
                                                        {submission.reviewed_by_name && (
                                                            <span className="text-green-700">
                                                                Approved by {submission.reviewed_by_name} {submission.reviewed_at && `· ${new Date(submission.reviewed_at).toLocaleDateString()}`}
                                                            </span>
                                                        )}
                                                        <span className="ml-auto flex items-center gap-2">
                                                            {submission.status === 'pending' && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-6 px-2 text-xs"
                                                                    onClick={(e) => { e.stopPropagation(); handleNotifyAdmins('submission', submission.name, `Submitted by ${submission.requester_name} from ${submission.state}`); }}
                                                                    data-testid={`notify-submission-${submission.submission_id}`}
                                                                >
                                                                    <Mail className="h-3 w-3 mr-1" />
                                                                    Notify
                                                                </Button>
                                                            )}
                                                            <span className="text-primary font-medium">Click to Review →</span>
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Resources Tab */}
                    <TabsContent value="resources">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle>Public Directory</CardTitle>
                                        <CardDescription>
                                            Manage directory resources
                                        </CardDescription>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                                await checkDirectoryStatus();
                                            }}
                                            className="gap-1"
                                            data-testid="check-directory-btn"
                                        >
                                            <Eye className="h-4 w-4" />
                                            Check Visibility
                                        </Button>
                                        {/* Export Buttons */}
                                        <div className="flex items-center gap-1 border-r border-border pr-2 mr-2">
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                onClick={() => handleExportResources('excel')}
                                                disabled={exporting || resources.length === 0}
                                                className="gap-1"
                                                data-testid="export-resources-excel"
                                            >
                                                <FileSpreadsheet className="h-4 w-4" />
                                                Excel
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                onClick={() => handleExportResources('pdf')}
                                                disabled={exporting || resources.length === 0}
                                                className="gap-1"
                                                data-testid="export-resources-pdf"
                                            >
                                                <FileText className="h-4 w-4" />
                                                PDF
                                            </Button>
                                        </div>
                                        <div className="relative w-full sm:w-64">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                placeholder="Search resources..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="pl-10"
                                                data-testid="search-resources"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* Directory visibility alert */}
                                {directoryStatus && directoryStatus.hidden_from_directory > 0 && (
                                    <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg" data-testid="directory-fix-alert">
                                        <div className="flex items-start gap-3">
                                            <Eye className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <h4 className="font-medium text-yellow-800">
                                                    {directoryStatus.hidden_from_directory} resources hidden from directory
                                                </h4>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    These resources appear here but not in the public directory.
                                                    {directoryStatus.breakdown.no_status_field > 0 && ` (${directoryStatus.breakdown.no_status_field} missing status field)`}
                                                    {directoryStatus.breakdown.status_pending > 0 && ` (${directoryStatus.breakdown.status_pending} pending)`}
                                                </p>
                                                <Button
                                                    size="sm"
                                                    className="mt-2 gap-1"
                                                    onClick={handleApproveAll}
                                                    disabled={fixingDirectory}
                                                    data-testid="approve-all-btn"
                                                >
                                                    {fixingDirectory ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <CheckCircle className="h-4 w-4" />
                                                    )}
                                                    Approve All and Make Visible
                                                </Button>
                                            </div>
                                            <Button variant="ghost" size="sm" onClick={() => setDirectoryStatus(null)}>
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {directoryStatus && directoryStatus.hidden_from_directory === 0 && (
                                    <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2" data-testid="directory-ok-alert">
                                        <CheckCircle className="h-5 w-5 text-green-600" />
                                        <span className="text-sm">All {directoryStatus.total_resources} resources are visible in the public directory.</span>
                                        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setDirectoryStatus(null)}>
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                                {loading ? (
                                    <div className="space-y-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Skeleton key={i} className="h-16 w-full" />
                                        ))}
                                    </div>
                                ) : filteredResources.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No resources found</p>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[500px]">
                                        <div className="space-y-2">
                                            {filteredResources.map((resource) => (
                                                <div 
                                                    key={resource.resource_id}
                                                    className="p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                                                    data-testid={`resource-row-${resource.resource_id}`}
                                                >
                                                    <div className="flex items-center justify-between gap-4">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <h4 className="font-medium truncate">{resource.name}</h4>
                                                                {resource.is_featured && (
                                                                    <Star className="h-4 w-4 text-amber-500 fill-amber-500 shrink-0" />
                                                                )}
                                                                {resource.mou_status && (
                                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${MOU_STATUS_COLORS[resource.mou_status] || 'bg-gray-100 text-gray-800'}`}>
                                                                        T&C: {resource.mou_status}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                                                <span className="flex items-center gap-1">
                                                                    <MapPin className="h-3 w-3" />
                                                                    {resource.state}
                                                                </span>
                                                                <span>
                                                                    {resource.organization_types?.slice(0, 2).join(', ')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {/* T&C Status Dropdown */}
                                                            <Select 
                                                                value={resource.mou_status || ''} 
                                                                onValueChange={(value) => handleUpdateMouStatus(resource.resource_id, value)}
                                                            >
                                                                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`mou-select-${resource.resource_id}`}>
                                                                    <FileCheck className="h-3 w-3 mr-1" />
                                                                    <SelectValue placeholder="T&C Status" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {MOU_STATUSES.map((status) => (
                                                                        <SelectItem key={status} value={status} className="text-xs">
                                                                            {status}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon"
                                                                onClick={() => setEditResource({...resource})}
                                                                title="Edit resource"
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </Button>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon"
                                                                onClick={() => handleToggleFeatured(resource)}
                                                                title={resource.is_featured ? 'Remove from featured' : 'Add to featured'}
                                                            >
                                                                <Star className={`h-4 w-4 ${resource.is_featured ? 'text-amber-500 fill-amber-500' : ''}`} />
                                                            </Button>
                                                            {resource.website && (
                                                                <a href={resource.website} target="_blank" rel="noopener noreferrer">
                                                                    <Button variant="ghost" size="icon">
                                                                        <ExternalLink className="h-4 w-4" />
                                                                    </Button>
                                                                </a>
                                                            )}
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon"
                                                                onClick={() => setDeleteResource(resource)}
                                                                className="text-destructive hover:text-destructive"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Users Management Tab */}
                    <TabsContent value="users">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Users className="h-5 w-5 text-primary" />
                                            Member Management
                                        </CardTitle>
                                        <CardDescription>
                                            Manage member accounts and admin roles
                                        </CardDescription>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="relative w-full sm:w-64">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                placeholder="Search members..."
                                                value={userSearchQuery}
                                                onChange={(e) => setUserSearchQuery(e.target.value)}
                                                className="pl-10"
                                                data-testid="search-users"
                                            />
                                        </div>
                                        <Button onClick={() => setShowCreateUser(true)} className="gap-2 shrink-0" data-testid="create-user-btn">
                                            <UserPlus className="h-4 w-4" />
                                            Add Member
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingUsers ? (
                                    <div className="space-y-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Skeleton key={i} className="h-16 w-full" />
                                        ))}
                                    </div>
                                ) : allUsers.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No users found</p>
                                        <Button onClick={fetchAllUsers} variant="outline" className="mt-4">
                                            Load Users
                                        </Button>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[500px]">
                                        <div className="space-y-2">
                                            {filteredUsers.map((u) => (
                                                <div 
                                                    key={u.user_id}
                                                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50"
                                                    data-testid={`user-${u.user_id}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        {u.picture ? (
                                                            <img 
                                                                src={u.picture} 
                                                                alt={u.name}
                                                                className="h-10 w-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                                <User className="h-5 w-5 text-primary" />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium">{u.name || 'No Name'}</span>
                                                                {u.is_admin && (
                                                                    <Badge variant="default" className="text-xs">Admin</Badge>
                                                                )}
                                                                {u.role === 'chapter_director' && (
                                                                    <Badge className="text-xs bg-amber-500 hover:bg-amber-600 text-white">
                                                                        Chapter Director
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-muted-foreground">{u.email}</p>
                                                            <div className="flex items-center gap-2">
                                                                {u.organization && (
                                                                    <p className="text-xs text-muted-foreground">{u.organization}</p>
                                                                )}
                                                                {u.chapter_region && u.role === 'chapter_director' && (
                                                                    <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">{u.chapter_region}</Badge>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                <span className={`inline-flex items-center gap-1 text-xs ${u.has_login ? 'text-green-700' : 'text-muted-foreground'}`}>
                                                                    {u.has_login ? (
                                                                        <CheckCircle className="h-3 w-3" />
                                                                    ) : (
                                                                        <XCircle className="h-3 w-3" />
                                                                    )}
                                                                    Login Created
                                                                </span>
                                                                <span className={`inline-flex items-center gap-1 text-xs ${u.terms_accepted ? 'text-green-700' : 'text-muted-foreground'}`}>
                                                                    {u.terms_accepted ? (
                                                                        <CheckCircle className="h-3 w-3" />
                                                                    ) : (
                                                                        <XCircle className="h-3 w-3" />
                                                                    )}
                                                                    T&C Accepted
                                                                    {u.terms_accepted_at && (
                                                                        <span className="text-[10px] text-muted-foreground ml-1">
                                                                            ({new Date(u.terms_accepted_at).toLocaleDateString()})
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {/* Approve/Reject membership */}
                                                        {!u.is_admin && (
                                                            u.approved ? (
                                                                <div className="flex items-center gap-2">
                                                                    <div className="text-right">
                                                                        <Badge variant="outline" className="text-xs text-green-700 border-green-300 cursor-pointer hover:bg-red-50 hover:text-red-700 hover:border-red-300" onClick={() => handleApproveUser(u.user_id, false)} data-testid={`revoke-${u.user_id}`}>
                                                                            Approved
                                                                        </Badge>
                                                                        {(u.approved_by_name || u.approved_at) && (
                                                                            <p className="text-[10px] text-muted-foreground mt-0.5">
                                                                                {u.approved_by_name && <>by {u.approved_by_name}</>}
                                                                                {u.approved_at && <> {u.approved_by_name ? '· ' : ''}{new Date(u.approved_at).toLocaleDateString()}</>}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <Button
                                                                    variant="default"
                                                                    size="sm"
                                                                    className="bg-green-600 hover:bg-green-700"
                                                                    onClick={() => handleApproveUser(u.user_id, true)}
                                                                    data-testid={`approve-user-${u.user_id}`}
                                                                >
                                                                    Approve
                                                                </Button>
                                                            )
                                                        )}
                                                        <Button
                                                            variant={u.is_admin ? "destructive" : "outline"}
                                                            size="sm"
                                                            onClick={() => handleToggleAdmin(u.user_id, u.is_admin)}
                                                            data-testid={`toggle-admin-${u.user_id}`}
                                                        >
                                                            {u.is_admin ? (
                                                                <>
                                                                    <XCircle className="h-4 w-4 mr-1" />
                                                                    Remove Admin
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Star className="h-4 w-4 mr-1" />
                                                                    Make Admin
                                                                </>
                                                            )}
                                                        </Button>
                                                        {!u.is_admin && u.role !== 'chapter_director' && (
                                                            <Select onValueChange={(region) => handleSetChapterDirector(u.user_id, region)}>
                                                                <SelectTrigger className="w-[180px] h-8 text-xs" data-testid={`set-cd-${u.user_id}`}>
                                                                    <SelectValue placeholder="Make Chapter Dir..." />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {US_STATES.map((state) => (
                                                                        <SelectItem key={state} value={state} className="text-xs">{state}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        )}
                                                        {u.role === 'chapter_director' && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                                                onClick={() => handleRemoveChapterDirector(u.user_id)}
                                                                data-testid={`remove-cd-${u.user_id}`}
                                                            >
                                                                <XCircle className="h-4 w-4 mr-1" />
                                                                Remove CD
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-muted-foreground hover:text-primary"
                                                            onClick={() => handleNotifyAdmins('user', u.name, `${u.email} — Needs approval review`)}
                                                            data-testid={`notify-user-${u.user_id}`}
                                                        >
                                                            <Mail className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-destructive hover:bg-destructive/10"
                                                            onClick={() => handleDeleteUser(u.user_id)}
                                                            data-testid={`delete-user-${u.user_id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>

                        {/* Create Member Dialog */}
                        <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>Add New Member</DialogTitle>
                                    <DialogDescription>Create a new member account. They will be pre-approved.</DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="new-user-name">Full Name *</Label>
                                        <Input id="new-user-name" placeholder="Jane Smith" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} data-testid="new-user-name" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="new-user-email">Email *</Label>
                                        <Input id="new-user-email" type="email" placeholder="jane@example.com" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} data-testid="new-user-email" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="new-user-password">Password (optional)</Label>
                                        <Input id="new-user-password" type="password" placeholder="Min 8 characters (or leave blank)" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} data-testid="new-user-password" />
                                        <p className="text-xs text-muted-foreground">If left blank, the member can log in with Google or set a password later.</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Role</Label>
                                        <Select value={newUser.role} onValueChange={v => setNewUser(p => ({ ...p, role: v }))}>
                                            <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="member">Member</SelectItem>
                                                <SelectItem value="chapter_director">Chapter Director</SelectItem>
                                                <SelectItem value="admin">Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {newUser.role === 'chapter_director' && (
                                        <div className="space-y-2">
                                            <Label htmlFor="new-user-region">Chapter Region</Label>
                                            <Input id="new-user-region" placeholder="e.g., Florida" value={newUser.chapter_region} onChange={e => setNewUser(p => ({ ...p, chapter_region: e.target.value }))} data-testid="new-user-region" />
                                        </div>
                                    )}
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancel</Button>
                                    <Button onClick={handleCreateUser} disabled={creatingUser} data-testid="confirm-create-user">
                                        {creatingUser ? 'Creating...' : 'Create Member'}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </TabsContent>

                    {/* Import Tab */}
                    <TabsContent value="import">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Upload className="h-5 w-5 text-primary" />
                                            Bulk Upload Organizations
                                        </CardTitle>
                                        <CardDescription>
                                            Upload a CSV or XLSX file to import organizations into the directory
                                        </CardDescription>
                                    </div>
                                    {importStep !== 'upload' && (
                                        <Button variant="outline" size="sm" onClick={resetImport} data-testid="import-reset-btn">
                                            Upload New File
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Step 1: Upload */}
                                {importStep === 'upload' && (
                                    <>
                                        <div 
                                            className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                                            onClick={() => fileInputRef.current?.click()}
                                            data-testid="upload-dropzone"
                                        >
                                            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                            <h3 className="font-medium mb-2">Upload CSV or XLSX File</h3>
                                            <p className="text-sm text-muted-foreground mb-4">
                                                Click to select a file. Your data will be previewed before importing.
                                            </p>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".csv,.xlsx,.xls"
                                                onChange={handleFileSelect}
                                                className="hidden"
                                                data-testid="file-input"
                                            />
                                            <Button disabled={previewLoading} data-testid="select-file-btn">
                                                {previewLoading ? (
                                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing...</>
                                                ) : (
                                                    'Select File'
                                                )}
                                            </Button>
                                        </div>

                                        <div className="bg-muted/50 p-4 rounded-lg">
                                            <h4 className="font-medium mb-2">Supported Formats</h4>
                                            <div className="grid sm:grid-cols-2 gap-4 text-sm text-muted-foreground">
                                                <div>
                                                    <p className="font-medium text-foreground mb-1">CSV Files</p>
                                                    <p>Standard comma-separated values with a header row</p>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-foreground mb-1">XLSX Files</p>
                                                    <p>Excel files with multiple sheets (each sheet imported separately)</p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-3">
                                                <strong>Required columns:</strong> Organization Name, State<br />
                                                <strong>Optional:</strong> Description, Organization Type, Website, City, Contact Name, Contact Email, Phone, Facebook, LinkedIn
                                            </p>
                                        </div>
                                    </>
                                )}

                                {/* Step 2: Preview */}
                                {importStep === 'preview' && previewData && (
                                    <>
                                        {/* Summary Cards */}
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                            <div className="bg-muted/50 p-3 rounded-lg text-center">
                                                <p className="text-2xl font-bold">{previewData.total_rows}</p>
                                                <p className="text-xs text-muted-foreground">Total Rows</p>
                                            </div>
                                            <div className="bg-green-500/10 p-3 rounded-lg text-center">
                                                <p className="text-2xl font-bold text-green-600">{previewData.valid_new}</p>
                                                <p className="text-xs text-muted-foreground">New to Import</p>
                                            </div>
                                            <div className="bg-yellow-500/10 p-3 rounded-lg text-center">
                                                <p className="text-2xl font-bold text-yellow-600">{previewData.duplicates}</p>
                                                <p className="text-xs text-muted-foreground">Duplicates (skip)</p>
                                            </div>
                                            <div className="bg-red-500/10 p-3 rounded-lg text-center">
                                                <p className="text-2xl font-bold text-red-600">{previewData.missing_state}</p>
                                                <p className="text-xs text-muted-foreground">Missing State</p>
                                            </div>
                                        </div>

                                        {/* States Breakdown */}
                                        {previewData.states_breakdown && Object.keys(previewData.states_breakdown).length > 0 && (
                                            <div className="bg-muted/50 p-4 rounded-lg">
                                                <h4 className="font-medium mb-2 text-sm">States Detected</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {Object.entries(previewData.states_breakdown).map(([state, count]) => (
                                                        <Badge key={state} variant="outline" className="text-xs">
                                                            {state}: {count}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Sample Rows Table */}
                                        <div>
                                            <h4 className="font-medium mb-2 text-sm">Sample Preview (first 10 rows)</h4>
                                            <div className="border border-border rounded-lg overflow-auto">
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="border-b bg-muted/50">
                                                            <th className="text-left p-2 font-medium">Organization</th>
                                                            <th className="text-left p-2 font-medium">State</th>
                                                            <th className="text-left p-2 font-medium">City</th>
                                                            <th className="text-left p-2 font-medium">Type</th>
                                                            <th className="text-left p-2 font-medium">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {previewData.sample_rows.map((row, i) => (
                                                            <tr key={i} className={`border-b last:border-0 ${row.is_duplicate ? 'opacity-50' : ''}`}>
                                                                <td className="p-2 max-w-[200px] truncate">{row.name}</td>
                                                                <td className="p-2">{row.state || <span className="text-red-500">Missing</span>}</td>
                                                                <td className="p-2">{row.city || '—'}</td>
                                                                <td className="p-2 max-w-[150px] truncate">{row.organization_types || '—'}</td>
                                                                <td className="p-2">
                                                                    {row.is_duplicate ? (
                                                                        <Badge variant="secondary" className="text-xs">Duplicate</Badge>
                                                                    ) : row.missing_state ? (
                                                                        <Badge variant="destructive" className="text-xs">No State</Badge>
                                                                    ) : (
                                                                        <Badge className="text-xs bg-green-500/80">New</Badge>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Confirm Import */}
                                        <div className="flex items-center justify-between pt-4 border-t">
                                            <p className="text-sm text-muted-foreground">
                                                <strong>{previewData.valid_new}</strong> new organizations will be added to the directory as approved resources.
                                            </p>
                                            <div className="flex gap-2">
                                                <Button variant="outline" onClick={resetImport} data-testid="cancel-import-btn">
                                                    Cancel
                                                </Button>
                                                <Button 
                                                    onClick={handleConfirmImport} 
                                                    disabled={importing || previewData.valid_new === 0}
                                                    className="gap-2"
                                                    data-testid="confirm-import-btn"
                                                >
                                                    {importing ? (
                                                        <><Loader2 className="h-4 w-4 animate-spin" />Importing...</>
                                                    ) : (
                                                        <>Import {previewData.valid_new} Organizations</>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Step 3: Result */}
                                {importStep === 'result' && importResult && (
                                    <div className={`p-6 rounded-lg ${importResult.error ? 'bg-destructive/10' : 'bg-green-500/10'}`} data-testid="import-result">
                                        {importResult.error ? (
                                            <p className="text-destructive font-medium">{importResult.error}</p>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <CheckCircle className="h-6 w-6 text-green-600" />
                                                    <h3 className="font-semibold text-lg">Import Complete</h3>
                                                </div>
                                                <div className="grid grid-cols-3 gap-4 mb-4">
                                                    <div>
                                                        <p className="text-2xl font-bold text-green-600">{importResult.imported}</p>
                                                        <p className="text-sm text-muted-foreground">Imported</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-2xl font-bold text-yellow-600">{importResult.skipped_duplicates || 0}</p>
                                                        <p className="text-sm text-muted-foreground">Skipped (duplicates)</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-2xl font-bold text-red-600">{importResult.total_errors || 0}</p>
                                                        <p className="text-sm text-muted-foreground">Errors</p>
                                                    </div>
                                                </div>
                                                {importResult.errors?.length > 0 && (
                                                    <div className="mt-3">
                                                        <p className="text-sm font-medium mb-1">Error details:</p>
                                                        <ScrollArea className="h-[150px]">
                                                            <ul className="text-sm text-muted-foreground space-y-1">
                                                                {importResult.errors.map((err, i) => (
                                                                    <li key={i}>- {err}</li>
                                                                ))}
                                                            </ul>
                                                        </ScrollArea>
                                                    </div>
                                                )}
                                                <Button className="mt-4" onClick={resetImport} data-testid="import-another-btn">
                                                    Upload Another File
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* News CMS Tab */}
                    <TabsContent value="news">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle>Resource Library CMS</CardTitle>
                                    <CardDescription>
                                        Manage news articles, reports, and resources
                                    </CardDescription>
                                </div>
                                <Button onClick={() => setShowAddNews(true)} className="gap-2">
                                    <Plus className="h-4 w-4" />
                                    Add Resource
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {newsResources.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Newspaper className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No news resources yet</p>
                                        <p className="text-sm">Click "Add Resource" to create your first entry</p>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[500px]">
                                        <div className="space-y-3">
                                            {newsResources.map((item) => (
                                                <div 
                                                    key={item.news_id}
                                                    className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className="font-medium truncate">{item.title}</h4>
                                                                {!item.is_published && (
                                                                    <Badge variant="outline">Draft</Badge>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                                <Badge variant="secondary" className="text-xs">
                                                                    {item.category}
                                                                </Badge>
                                                                <span>{item.source}</span>
                                                                <span className="flex items-center gap-1">
                                                                    {item.resource_type === 'link' ? (
                                                                        <LinkIcon className="h-3 w-3" />
                                                                    ) : (
                                                                        <FileUp className="h-3 w-3" />
                                                                    )}
                                                                    {item.resource_type}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {(item.url || item.file_url) && (
                                                                <a 
                                                                    href={item.url || item.file_url} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <Button variant="ghost" size="icon">
                                                                        <ExternalLink className="h-4 w-4" />
                                                                    </Button>
                                                                </a>
                                                            )}
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon"
                                                                onClick={() => setEditNews({...item})}
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </Button>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon"
                                                                onClick={() => setDeleteNews(item)}
                                                                className="text-destructive hover:text-destructive"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Export Tab */}
                    <TabsContent value="export">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Export Resources */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Building2 className="h-5 w-5 text-primary" />
                                        Public Directory
                                    </CardTitle>
                                    <CardDescription>
                                        Export all {resources.length} organizations in the directory
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-sm text-muted-foreground">
                                        Includes organization details, contact information, T&C status, and impact metrics.
                                    </p>
                                    <div className="flex gap-3">
                                        <Button 
                                            onClick={() => handleExportResources('excel')}
                                            disabled={exporting || resources.length === 0}
                                            className="flex-1 gap-2"
                                            data-testid="export-resources-excel-main"
                                        >
                                            {exporting ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <FileSpreadsheet className="h-4 w-4" />
                                            )}
                                            Export to Excel
                                        </Button>
                                        <Button 
                                            variant="outline"
                                            onClick={() => handleExportResources('pdf')}
                                            disabled={exporting || resources.length === 0}
                                            className="flex-1 gap-2"
                                            data-testid="export-resources-pdf-main"
                                        >
                                            {exporting ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <FileText className="h-4 w-4" />
                                            )}
                                            Export to PDF
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Export Members */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="h-5 w-5 text-primary" />
                                        Member Directory
                                    </CardTitle>
                                    <CardDescription>
                                        Export all registered HAAPE Hub members
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-sm text-muted-foreground">
                                        Includes member names, email addresses, organizations, and profile information.
                                    </p>
                                    <div className="flex gap-3">
                                        <Button 
                                            onClick={() => handleExportMembers('excel')}
                                            disabled={exporting}
                                            className="flex-1 gap-2"
                                            data-testid="export-members-excel"
                                        >
                                            {exporting ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <FileSpreadsheet className="h-4 w-4" />
                                            )}
                                            Export to Excel
                                        </Button>
                                        <Button 
                                            variant="outline"
                                            onClick={() => handleExportMembers('pdf')}
                                            disabled={exporting}
                                            className="flex-1 gap-2"
                                            data-testid="export-members-pdf"
                                        >
                                            {exporting ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <FileText className="h-4 w-4" />
                                            )}
                                            Export to PDF
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Export Info */}
                        <Card className="mt-6 bg-muted/50">
                            <CardContent className="py-4">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                                    <div className="text-sm text-muted-foreground">
                                        <p className="font-medium text-foreground">Export Information</p>
                                        <p>
                                            Excel exports include all available data fields and are best for data analysis. 
                                            PDF exports are formatted for printing and sharing, with a subset of key fields.
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Metrics Tab */}
                    <TabsContent value="metrics">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <TrendingUp className="h-5 w-5 text-primary" />
                                            Annual Impact Metrics
                                        </CardTitle>
                                        <CardDescription>
                                            Review and manage organization impact metric submissions
                                        </CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <Select value={metricsFilter} onValueChange={(v) => { setMetricsFilter(v); }}>
                                            <SelectTrigger className="w-[140px]" data-testid="metrics-filter-select">
                                                <SelectValue placeholder="Filter" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All</SelectItem>
                                                <SelectItem value="pending">Pending</SelectItem>
                                                <SelectItem value="approved">Approved</SelectItem>
                                                <SelectItem value="rejected">Rejected</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button onClick={() => fetchMetricsSubmissions()} variant="outline" size="sm" data-testid="metrics-refresh-btn">
                                            Refresh
                                        </Button>
                                        <Button onClick={() => setAdminMetricsDialog(true)} size="sm" className="gap-1" data-testid="admin-add-metrics-btn">
                                            <Plus className="h-4 w-4" />
                                            Add Metrics
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingMetrics ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
                                    </div>
                                ) : metricsSubmissions.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No metrics submissions found</p>
                                        <p className="text-sm mt-1">Organizations can submit their annual impact data from the "Submit Impact Metrics" page.</p>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[500px]">
                                        <div className="space-y-3">
                                            {metricsSubmissions.filter(m => metricsFilter === 'all' || m.status === metricsFilter).map((m) => (
                                                <div
                                                    key={m.metrics_id}
                                                    className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors"
                                                    data-testid={`metrics-row-${m.metrics_id}`}
                                                >
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                            <span className="font-mono font-bold text-lg text-primary">{m.year}</span>
                                                            <h4 className="font-medium truncate">{m.organization_name}</h4>
                                                            <Badge variant={m.status === 'approved' ? 'default' : m.status === 'rejected' ? 'destructive' : 'secondary'}>
                                                                {m.status}
                                                            </Badge>
                                                        </div>
                                                        {m.status === 'pending' && (
                                                            <div className="flex gap-2 shrink-0">
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleApproveMetrics(m.metrics_id)}
                                                                    disabled={processing}
                                                                    className="gap-1"
                                                                    data-testid={`approve-metrics-${m.metrics_id}`}
                                                                >
                                                                    <CheckCircle className="h-3.5 w-3.5" />
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="destructive"
                                                                    onClick={() => handleRejectMetrics(m.metrics_id)}
                                                                    disabled={processing}
                                                                    className="gap-1"
                                                                    data-testid={`reject-metrics-${m.metrics_id}`}
                                                                >
                                                                    <XCircle className="h-3.5 w-3.5" />
                                                                    Reject
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                                        {m.employed_count != null && (
                                                            <div className="bg-muted/50 p-2 rounded">
                                                                <p className="text-muted-foreground text-xs">Employed</p>
                                                                <p className="font-semibold">{m.employed_count}</p>
                                                            </div>
                                                        )}
                                                        {m.impacted_count != null && (
                                                            <div className="bg-muted/50 p-2 rounded">
                                                                <p className="text-muted-foreground text-xs">Impacted</p>
                                                                <p className="font-semibold">{m.impacted_count}</p>
                                                            </div>
                                                        )}
                                                        {m.completion_rate != null && (
                                                            <div className="bg-muted/50 p-2 rounded">
                                                                <p className="text-muted-foreground text-xs">Completion</p>
                                                                <p className="font-semibold">{m.completion_rate}%</p>
                                                            </div>
                                                        )}
                                                        {m.placement_rate != null && (
                                                            <div className="bg-muted/50 p-2 rounded">
                                                                <p className="text-muted-foreground text-xs">Placement</p>
                                                                <p className="font-semibold">{m.placement_rate}%</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {m.notes && (
                                                        <p className="text-sm text-muted-foreground mt-2 italic">"{m.notes}"</p>
                                                    )}
                                                    <p className="text-xs text-muted-foreground mt-2">
                                                        Submitted: {new Date(m.submitted_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Claims Tab */}
                    <TabsContent value="claims">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <ShieldCheck className="h-5 w-5 text-primary" />
                                            Organization Claims
                                        </CardTitle>
                                        <CardDescription>
                                            Review member requests to claim organizations, or manually assign members
                                        </CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <Select value={claimsFilter} onValueChange={setClaimsFilter}>
                                            <SelectTrigger className="w-[140px]" data-testid="claims-filter-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All</SelectItem>
                                                <SelectItem value="pending">Pending</SelectItem>
                                                <SelectItem value="approved">Approved</SelectItem>
                                                <SelectItem value="rejected">Rejected</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button onClick={fetchClaims} variant="outline" size="sm" data-testid="claims-refresh-btn">
                                            Refresh
                                        </Button>
                                        <Button onClick={() => setAssignDialog(true)} size="sm" className="gap-1" data-testid="assign-org-btn">
                                            <UserPlus className="h-4 w-4" />
                                            Assign Org
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingClaims ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
                                    </div>
                                ) : claims.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <ShieldCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No {claimsFilter !== 'all' ? claimsFilter : ''} claims found</p>
                                        <p className="text-sm mt-1">Members can claim organizations from the directory.</p>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[500px]">
                                        <div className="space-y-3">
                                            {claims.map((claim) => (
                                                <div
                                                    key={claim.claim_id}
                                                    className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors"
                                                    data-testid={`claim-row-${claim.claim_id}`}
                                                >
                                                    <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-3">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className="font-medium">{claim.organization_name}</h4>
                                                                <Badge variant={claim.status === 'approved' ? 'default' : claim.status === 'rejected' ? 'destructive' : 'secondary'}>
                                                                    {claim.status}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground">
                                                                Claimed by: <strong>{claim.user_name || claim.user_email}</strong>
                                                            </p>
                                                            {claim.role_at_org && (
                                                                <p className="text-sm mt-1">
                                                                    Role: <span className="font-medium">{claim.role_at_org}</span>
                                                                </p>
                                                            )}
                                                            {claim.justification && (
                                                                <p className="text-sm text-muted-foreground mt-1 italic">"{claim.justification}"</p>
                                                            )}
                                                            {claim.contact_email && (
                                                                <p className="text-xs text-muted-foreground mt-1">
                                                                    Contact: {claim.contact_email}
                                                                </p>
                                                            )}
                                                        </div>
                                                        {claim.status === 'pending' && (
                                                            <div className="flex gap-2 shrink-0">
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleApproveClaim(claim.claim_id)}
                                                                    disabled={processing}
                                                                    className="gap-1"
                                                                    data-testid={`approve-claim-${claim.claim_id}`}
                                                                >
                                                                    <CheckCircle className="h-3.5 w-3.5" />
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="destructive"
                                                                    onClick={() => handleRejectClaim(claim.claim_id)}
                                                                    disabled={processing}
                                                                    className="gap-1"
                                                                    data-testid={`reject-claim-${claim.claim_id}`}
                                                                >
                                                                    <XCircle className="h-3.5 w-3.5" />
                                                                    Reject
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        Submitted: {new Date(claim.created_at).toLocaleDateString()}
                                                        {claim.reviewed_at && ` | Reviewed: ${new Date(claim.reviewed_at).toLocaleDateString()}`}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Chapters Tab */}
                    <TabsContent value="chapters">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Shield className="h-5 w-5 text-amber-500" />
                                            Chapter Management
                                        </CardTitle>
                                        <CardDescription>
                                            Create and manage regional chapters with assigned directors
                                        </CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button onClick={fetchChapters} variant="outline" size="sm" data-testid="chapters-refresh-btn">
                                            <RefreshCw className="h-4 w-4" />
                                        </Button>
                                        <Button onClick={() => { setEditingChapter(null); setChapterForm({ name: '', state: '', description: '', director_id: '' }); setShowAddChapter(true); }} data-testid="add-chapter-btn">
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Chapter
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingChapters ? (
                                    <div className="space-y-3">
                                        {[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}
                                    </div>
                                ) : chapters.length === 0 ? (
                                    <div className="text-center py-12" data-testid="no-chapters">
                                        <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                                        <p className="text-muted-foreground mb-2">No chapters created yet</p>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Create regional chapters and assign directors to manage resources in their area.
                                        </p>
                                        <Button onClick={() => { setEditingChapter(null); setChapterForm({ name: '', state: '', description: '', director_id: '' }); setShowAddChapter(true); }} data-testid="add-first-chapter-btn">
                                            <Plus className="h-4 w-4 mr-1" />
                                            Create First Chapter
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {chapters.map((chapter) => (
                                            <div key={chapter.chapter_id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50" data-testid={`chapter-${chapter.chapter_id}`}>
                                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                                    <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center shrink-0">
                                                        <Shield className="h-6 w-6 text-amber-500" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-medium">{chapter.name}</span>
                                                            <Badge variant="outline" className="text-xs">{chapter.state}</Badge>
                                                            <Badge variant="secondary" className="text-xs">{chapter.resource_count} resources</Badge>
                                                        </div>
                                                        {chapter.description && (
                                                            <p className="text-sm text-muted-foreground mt-0.5 truncate">{chapter.description}</p>
                                                        )}
                                                        <div className="flex items-center gap-2 mt-1">
                                                            {chapter.director ? (
                                                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                    <User className="h-3 w-3" />
                                                                    Director: <strong>{chapter.director.name}</strong> ({chapter.director.email})
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-amber-600 flex items-center gap-1">
                                                                    <AlertCircle className="h-3 w-3" />
                                                                    No director assigned
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0 ml-4">
                                                    <Button variant="outline" size="sm" onClick={() => openEditChapter(chapter)} data-testid={`edit-chapter-${chapter.chapter_id}`}>
                                                        <Pencil className="h-4 w-4 mr-1" />
                                                        Edit
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteChapter(chapter.chapter_id)} data-testid={`delete-chapter-${chapter.chapter_id}`}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Email Preview Tab */}
                    <TabsContent value="emails">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Mail className="h-5 w-5" />
                                    Email Templates Preview
                                </CardTitle>
                                <CardDescription>
                                    Preview all branded email templates sent to applicants and members
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                    {[
                                        { key: 'nomination', name: 'Nomination Email', desc: 'Sent to nominated organizations', color: 'bg-teal-50 border-teal-200 text-teal-800' },
                                        { key: 'membership_application', name: 'Membership Application', desc: 'Sent to direct applicants', color: 'bg-blue-50 border-blue-200 text-blue-800' },
                                        { key: 'welcome', name: 'Welcome Email', desc: 'Sent on new registration', color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
                                        { key: 'needs_info', name: 'Needs More Info', desc: 'Sent when admin requests details', color: 'bg-amber-50 border-amber-200 text-amber-800' },
                                        { key: 'approval', name: 'Approval Email', desc: 'Sent on submission approval', color: 'bg-green-50 border-green-200 text-green-800' },
                                    ].map((tmpl) => (
                                        <button
                                            key={tmpl.key}
                                            onClick={() => handlePreviewEmail(tmpl.key)}
                                            className={`border rounded-xl p-4 text-left hover:shadow-md transition-shadow cursor-pointer ${tmpl.color}`}
                                            data-testid={`preview-email-${tmpl.key}`}
                                        >
                                            <p className="font-semibold text-sm">{tmpl.name}</p>
                                            <p className="text-xs mt-1 opacity-75">{tmpl.desc}</p>
                                        </button>
                                    ))}
                                </div>

                                {emailPreviewLoading && (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                    </div>
                                )}

                                {emailPreview && !emailPreviewLoading && (
                                    <div data-testid="email-preview-pane">
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <h3 className="font-semibold">{emailPreview.name}</h3>
                                                <p className="text-sm text-muted-foreground">Subject: {emailPreview.subject}</p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setEmailPreview(null)}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <div className="border rounded-xl overflow-hidden bg-white">
                                            <iframe
                                                srcDoc={emailPreview.html}
                                                title="Email Preview"
                                                className="w-full border-0"
                                                style={{ minHeight: '600px' }}
                                                sandbox=""
                                            />
                                        </div>
                                    </div>
                                )}

                                {!emailPreview && !emailPreviewLoading && (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                        <p>Select a template above to preview it</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Partners Tab */}
                    <TabsContent value="partners">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Database className="h-5 w-5 text-primary" />
                                            Partner Databases
                                        </CardTitle>
                                        <CardDescription>
                                            Manage trusted partner organizations and their databases
                                        </CardDescription>
                                    </div>
                                    <Button
                                        size="sm"
                                        className="gap-2"
                                        onClick={() => {
                                            setEditingPartner(null);
                                            setPartnerForm({ name: '', description: '', organization: '', logo_url: '', website: '', databases: [{ db_id: '', name: '', description: '', url: '', type: '' }] });
                                            setShowAddPartner(true);
                                        }}
                                        data-testid="add-partner-btn"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add Partner
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingPartners ? (
                                    <div className="space-y-3">
                                        {[1, 2].map(i => <Skeleton key={i} className="h-24" />)}
                                    </div>
                                ) : partnersList.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Database className="h-12 w-12 mx-auto mb-4 opacity-40" />
                                        <p>No partner databases yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {partnersList.map((p) => (
                                            <div key={p.partner_id} className="border rounded-lg p-4 hover:bg-muted/30" data-testid={`admin-partner-${p.partner_id}`}>
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h4 className="font-semibold">{p.name}</h4>
                                                            <Badge variant={p.is_active ? 'default' : 'secondary'} className="text-xs">
                                                                {p.is_active ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                                            {(p.databases || []).map(db => (
                                                                <Badge key={db.db_id} variant="outline" className="text-xs">{db.name}</Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 shrink-0">
                                                        <Button variant="outline" size="sm" onClick={() => openEditPartner(p)} data-testid={`edit-partner-${p.partner_id}`}>
                                                            <Pencil className="h-3 w-3" />
                                                        </Button>
                                                        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeletePartner(p.partner_id)} data-testid={`delete-partner-${p.partner_id}`}>
                                                            <Trash2 className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Add News Dialog */}
            <Dialog open={showAddNews} onOpenChange={setShowAddNews}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add News/Resource</DialogTitle>
                        <DialogDescription>
                            Add a new article, report, or resource to the News page
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Title *</Label>
                            <Input
                                value={newNewsItem.title}
                                onChange={(e) => setNewNewsItem(prev => ({...prev, title: e.target.value}))}
                                placeholder="Resource title"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Source *</Label>
                            <Input
                                value={newNewsItem.source}
                                onChange={(e) => setNewNewsItem(prev => ({...prev, source: e.target.value}))}
                                placeholder="e.g., HAAPE, Harvard Business Review"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Category</Label>
                                <Select 
                                    value={newNewsItem.category} 
                                    onValueChange={(v) => setNewNewsItem(prev => ({...prev, category: v}))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {NEWS_CATEGORIES.map((cat) => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Type</Label>
                                <Select 
                                    value={newNewsItem.resource_type} 
                                    onValueChange={(v) => setNewNewsItem(prev => ({...prev, resource_type: v}))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="link">External Link</SelectItem>
                                        <SelectItem value="upload">File Upload</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {newNewsItem.resource_type === 'link' ? (
                            <div className="space-y-2">
                                <Label>URL</Label>
                                <Input
                                    value={newNewsItem.url}
                                    onChange={(e) => setNewNewsItem(prev => ({...prev, url: e.target.value}))}
                                    placeholder="https://..."
                                />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Label>Upload File</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="file"
                                        accept=".pdf,.doc,.docx,image/*"
                                        onChange={(e) => handleNewsFileUpload(e, false)}
                                        disabled={uploadingNewsFile}
                                    />
                                    {uploadingNewsFile && <Loader2 className="h-4 w-4 animate-spin" />}
                                </div>
                                {newNewsItem.file_url && (
                                    <p className="text-xs text-muted-foreground">File uploaded successfully</p>
                                )}
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>Preview Image URL (optional)</Label>
                            <Input
                                value={newNewsItem.preview_image}
                                onChange={(e) => setNewNewsItem(prev => ({...prev, preview_image: e.target.value}))}
                                placeholder="https://..."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description (optional)</Label>
                            <Textarea
                                value={newNewsItem.description}
                                onChange={(e) => setNewNewsItem(prev => ({...prev, description: e.target.value}))}
                                placeholder="Brief description..."
                                rows={2}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="is_published"
                                checked={newNewsItem.is_published}
                                onCheckedChange={(v) => setNewNewsItem(prev => ({...prev, is_published: v}))}
                            />
                            <Label htmlFor="is_published">Publish immediately</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddNews(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateNews} disabled={processing}>
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Add Resource
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit News Dialog */}
            <Dialog open={!!editNews} onOpenChange={() => setEditNews(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Edit Resource</DialogTitle>
                    </DialogHeader>
                    {editNews && (
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Title</Label>
                                <Input
                                    value={editNews.title}
                                    onChange={(e) => setEditNews(prev => ({...prev, title: e.target.value}))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Source</Label>
                                <Input
                                    value={editNews.source}
                                    onChange={(e) => setEditNews(prev => ({...prev, source: e.target.value}))}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Category</Label>
                                    <Select 
                                        value={editNews.category} 
                                        onValueChange={(v) => setEditNews(prev => ({...prev, category: v}))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {NEWS_CATEGORIES.map((cat) => (
                                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Type</Label>
                                    <Select 
                                        value={editNews.resource_type} 
                                        onValueChange={(v) => setEditNews(prev => ({...prev, resource_type: v}))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="link">External Link</SelectItem>
                                            <SelectItem value="upload">File Upload</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>URL</Label>
                                <Input
                                    value={editNews.url || ''}
                                    onChange={(e) => setEditNews(prev => ({...prev, url: e.target.value}))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Textarea
                                    value={editNews.description || ''}
                                    onChange={(e) => setEditNews(prev => ({...prev, description: e.target.value}))}
                                    rows={2}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="edit_is_published"
                                    checked={editNews.is_published}
                                    onCheckedChange={(v) => setEditNews(prev => ({...prev, is_published: v}))}
                                />
                                <Label htmlFor="edit_is_published">Published</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditNews(null)}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateNews} disabled={processing}>
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete News Confirmation */}
            <AlertDialog open={!!deleteNews} onOpenChange={() => setDeleteNews(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Resource?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{deleteNews?.title}"? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteNews}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* View Submission Dialog */}
            <Dialog open={!!viewSubmission} onOpenChange={() => { setViewSubmission(null); setShowInfoRequest(false); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {viewSubmission && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 flex-wrap">
                                    {viewSubmission.name}
                                    <Badge 
                                        variant={
                                            viewSubmission.status === 'pending' ? 'secondary' :
                                            viewSubmission.status === 'needs_info' ? 'outline' :
                                            viewSubmission.status === 'approved' ? 'default' : 'destructive'
                                        }
                                    >
                                        {viewSubmission.status === 'needs_info' ? 'Needs Info' : viewSubmission.status}
                                    </Badge>
                                </DialogTitle>
                                <DialogDescription>
                                    Submitted by {viewSubmission.requester_name} ({viewSubmission.requester_email})
                                    {viewSubmission.requester_role && ` • ${viewSubmission.requester_role}`}
                                </DialogDescription>
                            </DialogHeader>
                            
                            <div className="space-y-4 py-4">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Mission</Label>
                                    <p className="mt-1">{viewSubmission.mission}</p>
                                </div>

                                <div>
                                    <Label className="text-xs text-muted-foreground">Stakeholder Type</Label>
                                    <p className="mt-1">{viewSubmission.stakeholder_type || 'Not specified'}</p>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Location</Label>
                                        <p className="mt-1">
                                            {viewSubmission.city ? `${viewSubmission.city}, ` : ''}{viewSubmission.state}
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Website</Label>
                                        <p className="mt-1">
                                            {viewSubmission.website ? (
                                                <a href={viewSubmission.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                                    {viewSubmission.website}
                                                </a>
                                            ) : 'N/A'}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Contact Name</Label>
                                        <p className="mt-1">{viewSubmission.contact_name || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Contact Email</Label>
                                        <p className="mt-1">{viewSubmission.contact_email || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Type-specific info */}
                                {viewSubmission.employer_type && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Employer Type</Label>
                                        <p className="mt-1">{viewSubmission.employer_type}</p>
                                    </div>
                                )}
                                {viewSubmission.job_types && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Job Types</Label>
                                        <p className="mt-1">{viewSubmission.job_types}</p>
                                    </div>
                                )}
                                {viewSubmission.services_for_employment && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Services for Employment</Label>
                                        <p className="mt-1">{viewSubmission.services_for_employment}</p>
                                    </div>
                                )}
                                {viewSubmission.program_description && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Program Description</Label>
                                        <p className="mt-1">{viewSubmission.program_description}</p>
                                    </div>
                                )}
                                {viewSubmission.government_support_description && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Government Support</Label>
                                        <p className="mt-1">{viewSubmission.government_support_description}</p>
                                    </div>
                                )}
                                {viewSubmission.other_info && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Other Information</Label>
                                        <p className="mt-1">{viewSubmission.other_info}</p>
                                    </div>
                                )}

                                {viewSubmission.info_request_message && (
                                    <div className="bg-orange-50 dark:bg-orange-950 p-3 rounded-lg">
                                        <Label className="text-xs text-orange-600">Info Requested</Label>
                                        <p className="mt-1 text-sm">{viewSubmission.info_request_message}</p>
                                    </div>
                                )}

                                {/* Applicant Replies Thread */}
                                {viewSubmission.replies && viewSubmission.replies.length > 0 && (
                                    <div className="space-y-2" data-testid="admin-replies-thread">
                                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                            <MessageSquare className="h-3 w-3" />
                                            Conversation ({viewSubmission.replies.length})
                                        </Label>
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {viewSubmission.replies.map((reply, idx) => (
                                                <div
                                                    key={reply.reply_id || idx}
                                                    className={`rounded-lg p-3 text-sm ${
                                                        reply.author_role === 'admin'
                                                            ? 'bg-primary/5 border border-primary/20'
                                                            : 'bg-blue-50 border border-blue-200'
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="font-medium text-xs">
                                                            {reply.author_role === 'admin' ? `${reply.author_name} (Admin)` : `${reply.author_name} (Applicant)`}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {new Date(reply.created_at).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <p className="whitespace-pre-wrap">{reply.message}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {['pending', 'needs_info'].includes(viewSubmission.status) && !showInfoRequest && (
                                    <div>
                                        <Label>Rejection Notes (if rejecting)</Label>
                                        <Textarea
                                            value={rejectNotes}
                                            onChange={(e) => setRejectNotes(e.target.value)}
                                            placeholder="Add notes if rejecting..."
                                            rows={2}
                                            className="mt-1"
                                        />
                                    </div>
                                )}

                                {showInfoRequest && (
                                    <div>
                                        <Label>Request More Information</Label>
                                        <Textarea
                                            value={infoRequestMessage}
                                            onChange={(e) => setInfoRequestMessage(e.target.value)}
                                            placeholder="What additional information do you need?"
                                            rows={3}
                                            className="mt-1"
                                            data-testid="info-request-textarea"
                                        />
                                    </div>
                                )}
                            </div>

                            {['pending', 'needs_info'].includes(viewSubmission.status) && (
                                <DialogFooter className="gap-2 flex-wrap">
                                    {showInfoRequest ? (
                                        <>
                                            <Button
                                                variant="outline"
                                                onClick={() => setShowInfoRequest(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={() => handleRequestInfo(viewSubmission.submission_id)}
                                                disabled={processing}
                                                data-testid="send-info-request-btn"
                                            >
                                                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />}
                                                Send Request
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                variant="outline"
                                                onClick={() => setShowInfoRequest(true)}
                                                data-testid="need-more-info-btn"
                                            >
                                                <MessageSquare className="h-4 w-4 mr-2" />
                                                Need More Info
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                onClick={() => handleReject(viewSubmission.submission_id)}
                                                disabled={processing}
                                                data-testid="reject-submission-btn"
                                            >
                                                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                                                Reject
                                            </Button>
                                            <Button
                                                onClick={() => handleApprove(viewSubmission.submission_id)}
                                                disabled={processing}
                                                data-testid="approve-submission-btn"
                                            >
                                                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                                                Approve
                                            </Button>
                                        </>
                                    )}
                                </DialogFooter>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Resource Dialog */}
            <Dialog open={!!editResource} onOpenChange={() => setEditResource(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {editResource && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Edit Resource</DialogTitle>
                                <DialogDescription>
                                    Update resource information
                                </DialogDescription>
                            </DialogHeader>
                            
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-name">Name</Label>
                                    <Input
                                        id="edit-name"
                                        value={editResource.name}
                                        onChange={(e) => setEditResource({...editResource, name: e.target.value})}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="edit-description">Description</Label>
                                    <Textarea
                                        id="edit-description"
                                        value={editResource.description}
                                        onChange={(e) => setEditResource({...editResource, description: e.target.value})}
                                        rows={3}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-state">State</Label>
                                        <Select 
                                            value={editResource.state} 
                                            onValueChange={(v) => setEditResource({...editResource, state: v})}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {US_STATES.map((state) => (
                                                    <SelectItem key={state} value={state}>{state}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-city">City</Label>
                                        <Input
                                            id="edit-city"
                                            value={editResource.city || ''}
                                            onChange={(e) => setEditResource({...editResource, city: e.target.value})}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="edit-website">Website</Label>
                                    <Input
                                        id="edit-website"
                                        value={editResource.website || ''}
                                        onChange={(e) => setEditResource({...editResource, website: e.target.value})}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Organization Types</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {ORGANIZATION_TYPES.map((type) => (
                                            <div key={type} className="flex items-center gap-2">
                                                <Checkbox
                                                    id={`edit-type-${type}`}
                                                    checked={editResource.organization_types?.includes(type)}
                                                    onCheckedChange={(checked) => {
                                                        const types = editResource.organization_types || [];
                                                        setEditResource({
                                                            ...editResource,
                                                            organization_types: checked 
                                                                ? [...types, type]
                                                                : types.filter(t => t !== type)
                                                        });
                                                    }}
                                                />
                                                <Label htmlFor={`edit-type-${type}`} className="text-sm font-normal">
                                                    {type}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-contact-name">Contact Name</Label>
                                        <Input
                                            id="edit-contact-name"
                                            value={editResource.contact_name || ''}
                                            onChange={(e) => setEditResource({...editResource, contact_name: e.target.value})}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-contact-email">Contact Email</Label>
                                        <Input
                                            id="edit-contact-email"
                                            value={editResource.contact_email || ''}
                                            onChange={(e) => setEditResource({...editResource, contact_email: e.target.value})}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="edit-featured"
                                        checked={editResource.is_featured}
                                        onCheckedChange={(checked) => setEditResource({...editResource, is_featured: checked})}
                                    />
                                    <Label htmlFor="edit-featured">Featured Resource</Label>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEditResource(null)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleUpdateResource} disabled={processing}>
                                    {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Save Changes
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Resource Dialog */}
            <AlertDialog open={!!deleteResource} onOpenChange={() => setDeleteResource(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Resource</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{deleteResource?.name}"? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteResource}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Admin Add Metrics Dialog */}
            <Dialog open={adminMetricsDialog} onOpenChange={setAdminMetricsDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Impact Metrics</DialogTitle>
                        <DialogDescription>
                            Manually enter or update metrics for any organization. Admin submissions are auto-approved.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Organization</Label>
                            <Select value={adminMetricsOrg} onValueChange={setAdminMetricsOrg}>
                                <SelectTrigger data-testid="admin-metrics-org-select">
                                    <SelectValue placeholder="Select organization" />
                                </SelectTrigger>
                                <SelectContent>
                                    {resources.map((r) => (
                                        <SelectItem key={r.resource_id} value={r.resource_id}>{r.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Year</Label>
                            <Select value={adminMetricsForm.year} onValueChange={(v) => setAdminMetricsForm(prev => ({ ...prev, year: v }))}>
                                <SelectTrigger data-testid="admin-metrics-year-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Employed Count</Label>
                                <Input
                                    type="number" min="0"
                                    placeholder="e.g., 150"
                                    value={adminMetricsForm.employed_count}
                                    onChange={e => setAdminMetricsForm(prev => ({ ...prev, employed_count: e.target.value }))}
                                    data-testid="admin-metrics-employed"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Impacted Count</Label>
                                <Input
                                    type="number" min="0"
                                    placeholder="e.g., 500"
                                    value={adminMetricsForm.impacted_count}
                                    onChange={e => setAdminMetricsForm(prev => ({ ...prev, impacted_count: e.target.value }))}
                                    data-testid="admin-metrics-impacted"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Completion Rate (%)</Label>
                                <Input
                                    type="number" min="0" max="100" step="0.1"
                                    placeholder="e.g., 85.5"
                                    value={adminMetricsForm.completion_rate}
                                    onChange={e => setAdminMetricsForm(prev => ({ ...prev, completion_rate: e.target.value }))}
                                    data-testid="admin-metrics-completion"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Placement Rate (%)</Label>
                                <Input
                                    type="number" min="0" max="100" step="0.1"
                                    placeholder="e.g., 72.0"
                                    value={adminMetricsForm.placement_rate}
                                    onChange={e => setAdminMetricsForm(prev => ({ ...prev, placement_rate: e.target.value }))}
                                    data-testid="admin-metrics-placement"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Textarea
                                placeholder="Any additional context..."
                                rows={2}
                                value={adminMetricsForm.notes}
                                onChange={e => setAdminMetricsForm(prev => ({ ...prev, notes: e.target.value }))}
                                data-testid="admin-metrics-notes"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAdminMetricsDialog(false)}>Cancel</Button>
                        <Button onClick={handleAdminSubmitMetrics} disabled={processing} data-testid="admin-metrics-submit-btn">
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Submit Metrics
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign Organization Dialog */}
            <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="h-5 w-5 text-primary" />
                            Assign Organization to Member
                        </DialogTitle>
                        <DialogDescription>
                            Manually grant a member edit access to an organization.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Organization</Label>
                            <Select value={assignOrgId} onValueChange={setAssignOrgId}>
                                <SelectTrigger data-testid="assign-org-select">
                                    <SelectValue placeholder="Select organization" />
                                </SelectTrigger>
                                <SelectContent>
                                    {resources.map((r) => (
                                        <SelectItem key={r.resource_id} value={r.resource_id}>{r.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Member Email</Label>
                            <Input
                                type="email"
                                placeholder="member@example.com"
                                value={assignEmail}
                                onChange={e => setAssignEmail(e.target.value)}
                                data-testid="assign-email-input"
                            />
                            <p className="text-xs text-muted-foreground">The member must already have a HAAPE Hub account.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignDialog(false)}>Cancel</Button>
                        <Button onClick={handleAssignOrg} disabled={processing || !assignOrgId || !assignEmail} data-testid="confirm-assign-btn">
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                            Assign
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add/Edit Chapter Dialog */}
            <Dialog open={showAddChapter} onOpenChange={(open) => { setShowAddChapter(open); if (!open) setEditingChapter(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingChapter ? 'Edit Chapter' : 'Create New Chapter'}</DialogTitle>
                        <DialogDescription>
                            {editingChapter ? 'Update chapter details and director assignment.' : 'Set up a new regional chapter with an optional director.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Chapter Name *</Label>
                            <Input
                                value={chapterForm.name}
                                onChange={(e) => setChapterForm({...chapterForm, name: e.target.value})}
                                placeholder="e.g. Florida Chapter"
                                data-testid="chapter-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>State/Region *</Label>
                            <Select 
                                value={chapterForm.state} 
                                onValueChange={(v) => setChapterForm({...chapterForm, state: v})}
                                disabled={!!editingChapter}
                            >
                                <SelectTrigger data-testid="chapter-state-select">
                                    <SelectValue placeholder="Select state" />
                                </SelectTrigger>
                                <SelectContent>
                                    {US_STATES.map((state) => (
                                        <SelectItem key={state} value={state}>{state}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {editingChapter && <p className="text-xs text-muted-foreground">State cannot be changed after creation.</p>}
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                value={chapterForm.description}
                                onChange={(e) => setChapterForm({...chapterForm, description: e.target.value})}
                                placeholder="Optional chapter description"
                                data-testid="chapter-description-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Assign Director</Label>
                            <Select 
                                value={chapterForm.director_id} 
                                onValueChange={(v) => setChapterForm({...chapterForm, director_id: v})}
                            >
                                <SelectTrigger data-testid="chapter-director-select">
                                    <SelectValue placeholder="Select a member (optional)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No Director</SelectItem>
                                    {allUsers.filter(u => !u.is_admin).map((u) => (
                                        <SelectItem key={u.user_id} value={u.user_id}>
                                            {u.name || u.email} {u.role === 'chapter_director' ? `(CD: ${u.chapter_region || '?'})` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">The selected member will be given the Chapter Director role for this state.</p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => { setShowAddChapter(false); setEditingChapter(null); }}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveChapter} data-testid="save-chapter-btn">
                            {editingChapter ? 'Save Changes' : 'Create Chapter'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add/Edit Partner Dialog */}
            <Dialog open={showAddPartner} onOpenChange={(open) => { if (!open) { setShowAddPartner(false); setEditingPartner(null); } }}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add Partner Database'}</DialogTitle>
                        <DialogDescription>
                            {editingPartner ? 'Update partner information' : 'Add a new trusted partner organization and their databases'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="partner-name">Partner Name *</Label>
                                <Input id="partner-name" value={partnerForm.name} onChange={e => setPartnerForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Stanford Neurodiversity Project" data-testid="partner-name-input" />
                            </div>
                            <div>
                                <Label htmlFor="partner-org">Organization</Label>
                                <Input id="partner-org" value={partnerForm.organization} onChange={e => setPartnerForm(p => ({ ...p, organization: e.target.value }))} placeholder="e.g. Stanford University" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="partner-desc">Description</Label>
                            <Textarea id="partner-desc" value={partnerForm.description} onChange={e => setPartnerForm(p => ({ ...p, description: e.target.value }))} placeholder="Why this partner is important..." rows={3} />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="partner-website">Website URL</Label>
                                <Input id="partner-website" value={partnerForm.website} onChange={e => setPartnerForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." />
                            </div>
                            <div>
                                <Label htmlFor="partner-logo">Logo URL (optional)</Label>
                                <Input id="partner-logo" value={partnerForm.logo_url} onChange={e => setPartnerForm(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." />
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-base font-semibold">Databases</Label>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPartnerForm(p => ({ ...p, databases: [...p.databases, { db_id: '', name: '', description: '', url: '', type: '' }] }))}
                                >
                                    <Plus className="h-3 w-3 mr-1" /> Add Database
                                </Button>
                            </div>
                            {partnerForm.databases.map((db, idx) => (
                                <div key={idx} className="border rounded-lg p-3 mb-3 space-y-2 bg-muted/30">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-muted-foreground">Database {idx + 1}</span>
                                        {partnerForm.databases.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0 text-red-500"
                                                onClick={() => setPartnerForm(p => ({ ...p, databases: p.databases.filter((_, i) => i !== idx) }))}
                                            >
                                                <X className="h-3 w-3" />
                                            </Button>
                                        )}
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-2">
                                        <Input
                                            value={db.name}
                                            onChange={e => { const dbs = [...partnerForm.databases]; dbs[idx] = { ...dbs[idx], name: e.target.value }; setPartnerForm(p => ({ ...p, databases: dbs })); }}
                                            placeholder="Database name"
                                        />
                                        <Input
                                            value={db.type}
                                            onChange={e => { const dbs = [...partnerForm.databases]; dbs[idx] = { ...dbs[idx], type: e.target.value }; setPartnerForm(p => ({ ...p, databases: dbs })); }}
                                            placeholder="Type (e.g. Resource Directory)"
                                        />
                                    </div>
                                    <Input
                                        value={db.url}
                                        onChange={e => { const dbs = [...partnerForm.databases]; dbs[idx] = { ...dbs[idx], url: e.target.value }; setPartnerForm(p => ({ ...p, databases: dbs })); }}
                                        placeholder="Database URL"
                                    />
                                    <Textarea
                                        value={db.description}
                                        onChange={e => { const dbs = [...partnerForm.databases]; dbs[idx] = { ...dbs[idx], description: e.target.value }; setPartnerForm(p => ({ ...p, databases: dbs })); }}
                                        placeholder="Brief description..."
                                        rows={2}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowAddPartner(false); setEditingPartner(null); }}>Cancel</Button>
                        <Button onClick={handleSavePartner} data-testid="save-partner-btn">{editingPartner ? 'Update Partner' : 'Add Partner'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDashboard;
